import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Box, Button, Link, Typography } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { InspiringSearchMoreCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/InspiringSearchMoreCardFragmentFragmentDoc';
import { buildUrl } from '@packages/shared/src/utils/buildUrl';
import { encodeSelectedFilterValues } from '../../queryEncoding';

/* GraphQL */ `
  fragment InspiringSearchMoreCardFragment on SuggestionPreview {
    query
    activeFilters {
      filterId
      activeValues {
        id
      }
    }
  }
`;

export type InspiringSearchMoreCardProps = {
  maskedSuggestion: FragmentType<typeof InspiringSearchMoreCardFragmentFragmentDoc>;
};

const messages = defineMessages({
  text: {
    id: 'search.inspiringSearch.previewPage.moreCard.text',
    defaultMessage: 'Entdecke alles in',
    description: 'Inspired search preview more card',
  },
  button: {
    id: 'search.inspiringSearch.previewPage.moreCard.button',
    defaultMessage: 'Zu {query}',
    description: 'Inspired search preview more card button',
  },
});

/**
 * A card leading to more results in the same search query suggestion, to continue the user journey after the initial preview.
 * */
export const InspiringSearchMoreCard: FC<InspiringSearchMoreCardProps> = ({ maskedSuggestion }) => {
  const { formatMessage } = useIntl();

  const { query, activeFilters } = getFragmentData(
    InspiringSearchMoreCardFragmentFragmentDoc,
    maskedSuggestion,
  );

  const compressedFilterValues = Object.fromEntries(
    activeFilters.map(({ filterId, activeValues }) => [filterId, activeValues.map(({ id }) => id)]),
  );
  const encodedFilters = encodeSelectedFilterValues(compressedFilterValues);
  const searchParams = new URLSearchParams(encodedFilters.f ? encodedFilters : undefined);

  const pathname = `/s/${encodeURIComponent(query.toLocaleLowerCase().trim()).replace(/%20/g, '+')}/`;
  const linkHref = buildUrl({ pathname, searchParams });

  return (
    <Link href={linkHref} underline="none">
      <Box
        sx={{
          height: '100%',
          backgroundColor: 'grey.light',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          <Box sx={{ marginBottom: 7 }}>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.dark' }}>
              {formatMessage(messages.text)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', fontWeight: 'bold', color: 'text.dark' }}
            >
              „{query}“
            </Typography>
          </Box>
          <Button color="primary">{formatMessage(messages.button, { query })}</Button>
        </Box>
      </Box>
    </Link>
  );
};
